import React, { useContext, useEffect, useRef, useState } from 'react'
import { TICKETS_TABLE_COLUMNS } from './ticket-table-columns'
import { TableNames } from 'types/table-column.types'
import { Box, Text, Table, Td, Tr, Th, Tbody, Thead, TableContainer } from '@chakra-ui/react'
import { TableContextProvider } from 'components/table-refactored/table-context'
import { ButtonsWrapper, CustomDivider, TableFooter } from 'components/table-refactored/table-footer'
import numeral from 'numeral'
import { TicketEditContext } from '../../pages/ticket-edit'
import { TicketInlineTableContainer } from './ticket-inline-table-container'
import TableColumnSettings from 'components/table/table-column-settings'
import { BlankSlate } from 'components/skeletons/skeleton-unit'
import { useGetAllTickets } from 'api/ticket'
import { useQuery } from 'react-query'
import { useClient } from 'utils/auth-context'
import { useNavigate } from 'react-router-dom'
import { useTableColumnSettings, useTableColumnSettingsUpdateMutation } from 'api/table-column-settings-refactored'
import { ExportButton } from 'components/table-refactored/export-button'
type Props = {
  propertyId: any
}

export const PROPERTY_FINANCIAL_OVERVIEW_KEY = 'PROPERTY_FINANCIAL_OVERVIEW'

const usePropertyFinancialOverview = propertyId => {
  const client = useClient()
  return useQuery([PROPERTY_FINANCIAL_OVERVIEW_KEY, propertyId], async () => {
    const response = await client(`ticket/property/${propertyId}/v1/financialOverview`, {})
    return { ...response?.data }
  })
}

export const TicketsInlineTable: React.FC<Props> = ({ propertyId }) => {
  const [selectedRow, setSelectedRow] = React.useState<number | null>();
  const { tableColumns: workOrderColumns, settingColumns } = useTableColumnSettings(
    TICKETS_TABLE_COLUMNS,
    TableNames.ticketsInlineGrid,
    {},
  )
  const { data: ticketsData, isLoading, refetch } = useGetAllTickets('', propertyId)

  const { setSelectedTicketId, selectedTicketId } = useContext(TicketEditContext)

  const { data: workOrderGridFinacialOverview, isLoading: workOrderGridFinacialOverviewIsLoading } = usePropertyFinancialOverview(propertyId)
  const navigate = useNavigate()

  const onRowClick = row => {
    navigate(`/property-details/${propertyId}/ticket/${row?.id}`, { replace: true })
    setSelectedTicketId(row?.id)
  }

  const { mutate: postGridColumn } = useTableColumnSettingsUpdateMutation(TableNames.ticketsInlineGrid)

  const onSave = (columns: any) => {
    postGridColumn(columns)
  }


  React.useEffect(() => {
    const selectedTicket = ticketsData ? ticketsData?.findIndex(element => Number(element.id) === Number(selectedTicketId)) : null;
    setSelectedRow(selectedTicket);
  }, [ticketsData])

  React.useEffect(() => {
    if (!ticketsData) return
    if (ticketsData.length === 0) return

    //setSelectedTicketId( ticketsData[0].id );
  }, [ticketsData])
  const boxRef = useRef<HTMLDivElement | null>(null);
const [contentOverflows, setContentOverflows] = useState(false);

useEffect(() => {
  const updateOverflow = () => {
    if (boxRef.current) {
      const boxContent = boxRef.current.firstChild;
      if (boxContent instanceof HTMLDivElement) {
        const contentHeight = boxContent.clientHeight;
        const containerHeight = boxRef.current?.clientHeight || 0;

        if (contentHeight > containerHeight) {
          setContentOverflows(true);
        } else {
          setContentOverflows(false);
        }
      }
    }
  };

  updateOverflow();
  window.addEventListener('resize', updateOverflow);
  return () => {
    window.removeEventListener('resize', updateOverflow);
  };
}, []);

  const SummaryDefaultJSX = workOrderGridFinacialOverviewIsLoading ? () => <BlankSlate size="sm" width="100%" /> : () => <></>; // HN-PSWOA-6824| SummaryDefaultJSX does conditional JSX render. If the API is in loading condition, it renders <BlankSlate /> otherwise return fragments

return (
  <>
  <Box
    overflowX="auto"
    overflowY={contentOverflows && (boxRef.current?.clientHeight || 0) > 565 ? 'auto' : 'hidden'}
    minHeight="600px"
    maxHeight="630px"
    maxWidth="100%"
    rounded={6}
    border="1px solid #CBD5E0"
    ref={boxRef}
    sx={{
      '&::-webkit-scrollbar': {
        width: '10px',
        height: '10px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#C2C7CD',
        borderRadius: '1px',
        border: '2px solid #f1f1f1',
      },
      scrollbarWidth: 'thin',
    }}
  >
  

     
        <TableContextProvider data={ticketsData} columns={workOrderColumns}>
          <TicketInlineTableContainer
            onRowClick={onRowClick}
            isLoading={isLoading}
            isEmpty={!isLoading && !ticketsData?.length}
            selectedRow={!Number.isNaN(selectedRow) ? selectedRow : null}
          />
          <TableFooter position="sticky" bottom="0" left="0" right="0" top="0">
            <ButtonsWrapper>
              <ExportButton columns={workOrderColumns} refetch={refetch} isLoading={isLoading} fileName="tickets" />
              <CustomDivider />
              {settingColumns && <TableColumnSettings disabled={isLoading} onSave={onSave} columns={settingColumns} />}
            </ButtonsWrapper>
          </TableFooter>
        </TableContextProvider>
      </Box>
      <Box mt="15px">
        <Text fontWeight="500" color="#000" fontSize="14px" as="h3" letterSpacing="0.5" mb="3px">
          Summary
        </Text>
        <Box>
          <TableContainer
            sx={{
              '&::-webkit-scrollbar': {
                width: '6px',
                height: '10px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#C2C7CD',
                borderRadius: '1px',
                border: '2px solid #f1f1f1',
              },
              scrollbarWidth: 'thin',
            }}
          >
            <Table size="md" roundedTop={3} height="70px">
              <Thead backgroundColor="#F2F3F4 !important">
                <Tr backgroundColor="#F2F3F4 !important">
                  <Th
                    borderBottom="1px solid #CBD5E0"
                    color="#4A5568"
                    fontSize="12px"
                    fontWeight="500"
                    isTruncated
                    textTransform="none"
                  >
                    Approved SOW
                  </Th>
                  <Th
                    borderBottom="1px solid #CBD5E0"
                    color="#4A5568"
                    fontSize="12px"
                    fontWeight="500"
                    isTruncated
                    textTransform="none"
                  >
                    Material
                  </Th>
                  <Th
                    borderBottom="1px solid #CBD5E0"
                    color="#4A5568"
                    fontSize="12px"
                    fontWeight="500"
                    isTruncated
                    textTransform="none"
                  >
                    Vendor Cost
                  </Th>
                  <Th
                    borderBottom="1px solid #CBD5E0"
                    color="#4A5568"
                    fontSize="12px"
                    fontWeight="500"
                    isTruncated
                    textTransform="none"
                  >
                    Profit
                  </Th>
                  <Th
                    borderBottom="1px solid #CBD5E0"
                    color="#4A5568"
                    fontSize="12px"
                    fontWeight="500"
                    isTruncated
                    textTransform="none"
                  >
                    Margin
                  </Th>
                  <Th
                    borderBottom="1px solid #CBD5E0"
                    color="#4A5568"
                    fontSize="12px"
                    fontWeight="500"
                    isTruncated
                    textTransform="none"
                  >
                    Change Order
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td
                    marginTop="0"
                    marginBottom="0"
                    paddingTop="0"
                    paddingBottom="0"
                    fontWeight="400"
                    fontSize="12px"
                    color="#4A5568"
                    isTruncated
                  >
                    {workOrderGridFinacialOverview ? (
                      numeral(workOrderGridFinacialOverview?.approvedSOW).format('$0,0.00')
                    ) : (
                     <SummaryDefaultJSX />
                    )}
                  </Td>
                  <Td
                    marginTop="0"
                    marginBottom="0"
                    paddingTop="0"
                    paddingBottom="0"
                    fontWeight="400"
                    fontSize="12px"
                    color="#4A5568"
                    isTruncated
                  >
                    {workOrderGridFinacialOverview ? (
                      numeral(workOrderGridFinacialOverview?.material).format('$0,0.00')
                    ) : (
                     <SummaryDefaultJSX />
                    )}
                  </Td>
                  <Td
                    marginTop="0"
                    marginBottom="0"
                    paddingTop="0"
                    paddingBottom="0"
                    fontWeight="400"
                    fontSize="12px"
                    color="#4A5568"
                    isTruncated
                  >
                    {workOrderGridFinacialOverview ? (
                      numeral(workOrderGridFinacialOverview?.vendorCost).format('$0,0.00')
                    ) : (
                     <SummaryDefaultJSX />
                    )}
                  </Td>
                  <Td
                    marginTop="0"
                    marginBottom="0"
                    paddingTop="0"
                    paddingBottom="0"
                    fontWeight="400"
                    fontSize="12px"
                    color="#4A5568"
                    isTruncated
                  >
                    {workOrderGridFinacialOverview ? (
                      numeral(workOrderGridFinacialOverview?.profit).format('$0,0.00')
                    ) : (
                     <SummaryDefaultJSX />
                    )}
                  </Td>

                  <Td
                    marginTop="0"
                    marginBottom="0"
                    paddingTop="0"
                    paddingBottom="0"
                    fontWeight="400"
                    fontSize="12px"
                    color="#4A5568"
                    isTruncated
                  >
                    {workOrderGridFinacialOverview ? (
                      numeral(workOrderGridFinacialOverview?.profitMargin / 100).format('0.00%')
                    ) : (
                     <SummaryDefaultJSX />
                    )}
                  </Td>
                  <Td
                    marginTop="0"
                    marginBottom="0"
                    paddingTop="0"
                    paddingBottom="0"
                    fontWeight="400"
                    fontSize="12px"
                    color="#4A5568"
                    isTruncated
                  >
                    {workOrderGridFinacialOverview ? (
                      numeral(workOrderGridFinacialOverview?.changeOrder).format('$0,0.00')
                    ) : (
                     <SummaryDefaultJSX />
                    )}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  )
}
